<template>
  <v-card class="elevation-1">
    <v-card-text>
      <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
      
      <!-- Job Definition Selector -->
      <div class="mb-4">
        <v-select
          v-model="selectedJobDefinitionId"
          :items="jobDefinitions"
          :item-text="item => item.job_definition_title || item.title || 'Untitled'"
          :item-value="item => item.job_definition_id"
          :label="$t('job_definition')"
          outlined
          dense
          clearable
          :disabled="loading"
          @change="loadDataForJobDefinition"
          return-object
        >
          <template v-slot:prepend>
            <v-icon>mdi-file-tree</v-icon>
          </template>
          <template v-slot:selection="{ item }">
            {{ item.job_definition_title || item.title || 'Untitled' }}
          </template>
        </v-select>
      </div>
      
      <div v-if="!loading && selectedJobDefinitionId" class="map-container">
        <table class="config-map-table">
          <thead>
            <tr>
              <th class="entity-header sticky-col">{{ $t('status') }}</th>
              <th 
                v-for="(pool, index) in filteredPools" 
                :key="`pool-${pool.pool_id}`"
                class="pool-header"
                :class="{ 'pool-header-alternate': index % 2 !== 0 }"
              >
                {{ pool.pool_title }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="status in filteredStatuses" :key="status.status_id">
              <td class="entity-cell sticky-col">
                <v-chip small :color="getStatusTypeColor(status.status_type)" text-color="black">
                  {{ status.status_title }}
                </v-chip>
              </td>
              <td 
                v-for="(pool, index) in filteredPools"
                :key="`${status.status_id}-pool-${pool.pool_id}`"
                class="status-cell pool-status-col"
                :class="{ 'pool-status-col-alternate': index % 2 !== 0 }"
              >
                <div v-if="isStatusInPool(status.status_id, pool.pool_id)">
                  <v-icon 
                    color="primary"
                    small
                  >
                    mdi-check-circle
                  </v-icon>
                  <div v-if="getNextStatuses(status.status_id, pool.pool_id).length > 0" class="next-status-box">
                    <div 
                      class="next-status-header" 
                      @click="toggleNextStatus(status.status_id, pool.pool_id)"
                    >
                      <span>Next Status ({{ getNextStatuses(status.status_id, pool.pool_id).length }})</span>
                      <v-icon small>
                        {{ isNextStatusExpanded(status.status_id, pool.pool_id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      </v-icon>
                    </div>
                    <div v-show="isNextStatusExpanded(status.status_id, pool.pool_id)" class="next-status-chips-container">
                      <v-chip 
                        v-for="nextStatus in getNextStatuses(status.status_id, pool.pool_id)" 
                        :key="nextStatus.next_status_id"
                        x-small 
                        :color="getNextStatusColor(nextStatus)"
                        text-color="black"
                        class="next-status-chip"
                      >
                        {{ getNextStatusLabel(nextStatus) }}
                      </v-chip>
                    </div>
                  </div>
                </div>
                <span v-else class="grey--text text--lighten-1">—</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="filteredStatuses.length === 0 && selectedJobDefinitionId" class="text-center pa-8">
          <v-icon size="64" color="grey lighten-1">mdi-information-outline</v-icon>
          <p class="mt-4 grey--text">{{ $t('no_data_available') }}</p>
        </div>
      </div>

      <div v-if="!loading && !selectedJobDefinitionId" class="text-center pa-8">
        <v-icon size="64" color="grey lighten-1">mdi-arrow-up-circle-outline</v-icon>
        <p class="mt-4 grey--text">{{ $t('select_job_definition_to_view_map') }}</p>
      </div>

      <!-- Legend and Export Button -->
      <div v-if="selectedJobDefinitionId" class="d-flex align-start mt-4" style="gap: 16px;">
        <v-card class="flex-grow-1 legend-card" outlined>
          <div class="legend-title">{{ $t('legend') }}</div>
          <v-card-text class="pt-6">
            <div class="legend-container">
              <div class="legend-item">
                <v-chip x-small color="grey lighten-1" text-color="black" class="mr-2">{{ $t('job_waiting') }}</v-chip>
              </div>
              <div class="legend-item">
                <v-chip x-small color="orange lighten-2" text-color="black" class="mr-2">{{ $t('job_doing') }}</v-chip>
              </div>
              <div class="legend-item">
                <v-chip x-small color="green lighten-2" text-color="black" class="mr-2">{{ $t('job_done') }}</v-chip>
              </div>
              <div class="legend-item">
                <v-chip x-small color="purple lighten-2" text-color="black" class="mr-2">{{ $t('job_canceled') }}</v-chip>
              </div>
              <div class="legend-item">
                <v-icon color="primary" small class="mr-2">mdi-check-circle</v-icon>
                <span class="caption">{{ $t('status') }} {{ $t('in') }} {{ $t('pool') }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Export Button -->
        <v-btn
          color="primary"
          @click="exportCSV"
          :disabled="loading || filteredStatuses.length === 0"
          class="mt-2"
        >
          <v-icon left>mdi-download</v-icon>
          {{ $t('export') }}
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.map-container {
  overflow: auto;
  max-width: 100%;
  max-height: calc(100vh - 400px);
  position: relative;
}

.config-map-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.config-map-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

.config-map-table th,
.config-map-table td {
  border: 1px solid #e0e0e0;
  padding: 8px;
  text-align: center;
}

.entity-header {
  background-color: #1976d2;
  color: white;
  font-weight: 600;
  text-align: left !important;
  padding: 8px 6px !important;
}

.pool-header {
  background-color: #1976d2;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
}

.pool-status-col {
  background-color: #e3f2fd;
}

.pool-status-col-alternate {
  background-color: white;
}

.pool-header-alternate {
  background-color: #1565c0;
}

.entity-cell {
  text-align: left !important;
  font-weight: 500;
  background-color: #fafafa;
  padding: 8px 6px !important;
}

.status-cell {
  vertical-align: top;
  padding-top: 12px !important;
}

.next-status-box {
  background-color: white;
  border-radius: 6px;
  padding: 4px 6px;
  margin-top: 6px;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.next-status-header {
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  margin-bottom: 2px;
  padding: 2px 0;
  padding-bottom: 3px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.next-status-header:hover {
  background-color: #f5f5f5;
  border-radius: 3px;
  padding: 2px 3px;
  margin: -2px -3px 1px -3px;
}

.next-status-chips-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 2px;
}

.next-status-chip {
  margin: 0 !important;
  align-self: flex-start;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: #fafafa;
}

.config-map-table thead th.sticky-col {
  z-index: 3;
  background-color: #1976d2;
}

.legend-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}

.legend-item {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.legend-card {
  position: relative;
}

.legend-title {
  position: absolute;
  top: -10px;
  left: 16px;
  background-color: white;
  padding: 0 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  z-index: 1;
}
</style>

<script>
import JobDefinitionAPI from '@/services/api/job/job_definition'
import JobPoolStatusAPI from '@/services/api/job/job_pool_status'
import PoolAPI from '@/services/api/job/pool'
import StatusAPI from '@/services/api/job/status'
import NextStatusAPI from '@/services/api/job/next_status'

export default {
  name: "MapByStatus",

  props: {
    appTenantId: {
      type: [String, Number],
      default: null
    }
  },

  computed: {
    filteredStatuses() {
      if (!this.search) return this.statuses
      
      const searchLower = this.search.toLowerCase()
      return this.statuses.filter(status => 
        status.status_title.toLowerCase().includes(searchLower)
      )
    },

    filteredPools() {
      return this.pools
    }
  },

  data() {
    return {
      loading: false,
      search: '',
      jobDefinitions: [],
      selectedJobDefinitionId: null,
      selectedJobDefinition: null,
      pools: [],
      statuses: [],
      jobPoolStatuses: [],
      nextStatusMap: new Map(), // Maps job_pool_status_id to next statuses
      expandedNextStatuses: new Set() // Tracks which next status boxes are expanded
    }
  },

  mounted() {
    this.loadJobDefinitions()
  },

  methods: {
    async loadJobDefinitions() {
      this.loading = true

      try {
        const params = {}
        if (this.appTenantId) {
          params.tenant_id = this.appTenantId
        }
        const result = await JobDefinitionAPI.getAllJobDefinitions(params)
        this.jobDefinitions = result.data.items || []
        
        // Sort by title
        this.jobDefinitions.sort((a, b) => 
          (a.job_definition_title || '').localeCompare(b.job_definition_title || '')
        )
        
        // Auto-select first job definition if available
        if (this.jobDefinitions.length > 0) {
          this.selectedJobDefinitionId = this.jobDefinitions[0]
          await this.loadDataForJobDefinition()
        }
      } catch (error) {
        console.error('Error loading job definitions:', error)
      } finally {
        this.loading = false
      }
    },

    async loadDataForJobDefinition() {
      
      if (!this.selectedJobDefinitionId) {
        this.pools = []
        this.statuses = []
        this.jobPoolStatuses = []
        return
      }

      this.loading = true

      try {
        // Get the job_definition_id from the selected object
        const jobDefId = this.selectedJobDefinitionId.job_definition_id
        
        if (!jobDefId) {
          console.error('Invalid job definition ID:', this.selectedJobDefinitionId)
          this.loading = false
          return
        }
        
        // Load job-pool-status relationships for the selected job definition
        // This API returns pool and status data embedded in the response
        const result = await JobPoolStatusAPI.getJobPoolStatusByJobDefinition(jobDefId)
        
        this.jobPoolStatuses = result.data.items || []

        // Extract unique pool IDs and status IDs
        const poolIds = [...new Set(this.jobPoolStatuses.map(jps => jps.pool_id).filter(id => id))]
        const statusIds = [...new Set(this.jobPoolStatuses.map(jps => jps.status_id).filter(id => id))]

        // Fetch pool and status details
        const poolPromises = poolIds.map(id => PoolAPI.getPool(id).catch(err => {
          console.error('Error fetching pool', id, err)
          return null
        }))
        const statusPromises = statusIds.map(id => StatusAPI.getStatus(id).catch(err => {
          console.error('Error fetching status', id, err)
          return null
        }))

        const [poolResults, statusResults] = await Promise.all([
          Promise.all(poolPromises),
          Promise.all(statusPromises)
        ])

        // Extract data from results
        this.pools = poolResults
          .filter(result => result && result.data)
          .map(result => result.data)
          .sort((a, b) => (a.pool_title || '').localeCompare(b.pool_title || ''))
        
        this.statuses = statusResults
          .filter(result => result && result.data)
          .map(result => result.data)
          .sort((a, b) => (a.status_title || '').localeCompare(b.status_title || ''))

        // Load next_status relationships for each job_pool_status
        await this.loadNextStatuses()

      } catch (error) {
        console.error('Error loading job definition data:', error)
      } finally {
        this.loading = false
      }
    },

    async loadNextStatuses() {
      this.nextStatusMap.clear()

      const nextStatusPromises = this.jobPoolStatuses.map(async jps => {
        try {
          const result = await NextStatusAPI.getByCurrentPoolStatus(jps.job_pool_status_id)
          const nextStatuses = result.data.items || []
          
          if (nextStatuses.length > 0) {
            this.nextStatusMap.set(jps.job_pool_status_id, nextStatuses)
          }
        } catch (error) {
          console.error('Error loading next status for', jps.job_pool_status_id, error)
        }
      })

      await Promise.all(nextStatusPromises)
    },

    getJobPoolStatusId(statusId, poolId) {
      const jps = this.jobPoolStatuses.find(jps => 
        jps.status_id === statusId && jps.pool_id === poolId
      )
      return jps?.job_pool_status_id
    },

    getNextStatuses(statusId, poolId) {
      const jobPoolStatusId = this.getJobPoolStatusId(statusId, poolId)
      if (!jobPoolStatusId) return []
      
      return this.nextStatusMap.get(jobPoolStatusId) || []
    },

    getNextStatusText(nextStatus) {
      // Find the next job_pool_status to get pool and status info
      const nextJps = this.jobPoolStatuses.find(jps => 
        jps.job_pool_status_id === nextStatus.next_job_pool_status_id
      )
      
      if (!nextJps) return ''
      
      const pool = this.pools.find(p => p.pool_id === nextJps.pool_id)
      const status = this.statuses.find(s => s.status_id === nextJps.status_id)
      
      return `${pool?.pool_title || 'Pool ' + nextJps.pool_id} / ${status?.status_title || 'Status ' + nextJps.status_id}`
    },

    isStatusInPool(statusId, poolId) {
      return this.jobPoolStatuses.some(jps => 
        jps.status_id === statusId && jps.pool_id === poolId
      )
    },

    getStatusTypeColor(type) {
      const colors = {
        'waiting': 'grey lighten-1',
        'doing': 'orange lighten-2',
        'done': 'green lighten-2',
        'canceled': 'purple lighten-2'
      }
      return colors[type] || 'grey'
    },

    getNextStatusColor(nextStatus) {
      // Find the next job_pool_status to get status info
      const nextJps = this.jobPoolStatuses.find(jps => 
        jps.job_pool_status_id === nextStatus.next_job_pool_status_id
      )
      
      if (!nextJps) return 'grey'
      
      const status = this.statuses.find(s => s.status_id === nextJps.status_id)
      
      return status ? this.getStatusTypeColor(status.status_type) : 'grey'
    },

    getNextStatusLabel(nextStatus) {
      // Find the next job_pool_status to get pool and status info
      const nextJps = this.jobPoolStatuses.find(jps => 
        jps.job_pool_status_id === nextStatus.next_job_pool_status_id
      )
      
      if (!nextJps) return ''
      
      const pool = this.pools.find(p => p.pool_id === nextJps.pool_id)
      const status = this.statuses.find(s => s.status_id === nextJps.status_id)
      
      return `${pool?.pool_title || 'Pool ' + nextJps.pool_id} - ${status?.status_title || 'Status ' + nextJps.status_id}`
    },

    toggleNextStatus(statusId, poolId) {
      const key = `${statusId}-${poolId}`
      if (this.expandedNextStatuses.has(key)) {
        this.expandedNextStatuses.delete(key)
      } else {
        this.expandedNextStatuses.add(key)
      }
      // Force re-render
      this.$forceUpdate()
    },

    isNextStatusExpanded(statusId, poolId) {
      const key = `${statusId}-${poolId}`
      return this.expandedNextStatuses.has(key)
    },

    exportCSV() {
      const jobDefTitle = this.selectedJobDefinitionId 
        ? (this.selectedJobDefinitionId.job_definition_title || this.selectedJobDefinitionId.title || 'unknown')
        : 'unknown'
      
      // Prepare CSV data
      const rows = []
      
      // Build header row
      const header = [this.$t('status')]
      this.filteredPools.forEach(pool => {
        header.push(pool.pool_title)
      })
      rows.push(header)

      // Build data rows
      this.filteredStatuses.forEach(status => {
        const row = [status.status_title]
        
        this.filteredPools.forEach(pool => {
          const isInPool = this.isStatusInPool(status.status_id, pool.pool_id)
          row.push(isInPool ? 'Yes' : 'No')
        })
        
        rows.push(row)
      })

      // Convert to CSV string
      const csvContent = rows.map(row => 
        row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          const cellStr = String(cell || '')
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`
          }
          return cellStr
        }).join(',')
      ).join('\n')

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', `status_map_${jobDefTitle}_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>
